"use client"

import React, { useEffect, useState } from "react"
import { Box, Flex, Text } from "@chakra-ui/react"
import { productView } from "utils/gtm"
import { LoadingMask } from "components/LoadingMask"
import { PreviewAlertBox } from "components/Alert/PreviewAlert"
import { pdpQuantityVar } from "lib/apollo/variables"
import { useSegment } from "tracking"
import { getPDPUrl } from "utils/getPDPUrl"
import { siteUrl } from "utils/getSiteUrl"
import { useLocale } from "hooks"
import { useRouter } from "lib/router"
import { addMembershipById } from "lib/api"
import { useUser } from "lib/user"
import type { VariantType } from "types"
import { ATBConfirmation } from "basket_and_checkout/shared/components/ATBConfirmation"
import {
  GalleryLayout,
  ProductInfoLayout,
} from "components/Shop/PDPPage/PDPHeader/PDPHeaderLayout"
import { ErrorBoundary } from "react-error-boundary"
import { PDPHeaderFallback } from "components/Shop/PDPPage/PDPHeader/PDPHeaderFallback"
import { Logger } from "lib/logger"
import { useCurrencyCode } from "hooks/useCurrencyCode"

import { useHasMounted } from "utils/wrappers"
import { ReadMoreV2 } from "components/ReadMoreV2"
import { Pricing } from "basket_and_checkout/shared/components/PDP/Pricing/Pricing"
import { getShowAccessPassPopup } from "basket_and_checkout/shared/components/PDP/Pricing/utils"
import type { PurchaseType } from "../types"
import { ProductGalleryV2 } from "../ProductGalleryV2/ProductGalleryV2"

export const GiftMembershipPDP: React.FC<{
  variant: VariantType
  preview: boolean
  eventId: string
}> = ({ variant, preview, eventId }) => {
  const { track } = useSegment()
  const currencyCode = useCurrencyCode()

  const [confirmation, setConfirmation] = useState(false)
  const [purchaseType, setPurchaseType] = useState<PurchaseType>("member")

  useEffect(() => {
    const hasNonMemberPricesPreferred = getShowAccessPassPopup()
    if (hasNonMemberPricesPreferred) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      setPurchaseType("non-member")
    }
  }, [])

  const router = useRouter()
  const { membershipId } = router.query
  const locale = useLocale()
  const user = useUser()

  useEffect(() => {
    pdpQuantityVar(1)
    const {
      sku,
      title: name,
      brandName: brand,
      subTitle,
      pricing: { members, typical },
    } = variant

    track(
      {
        event: "Product Viewed",
        properties: {
          currency: locale === "en-GB" ? "GBP" : "USD",
          sku,
          name,
          brand,
          variant: subTitle || "",
          price: user.hasMembership ? members : typical,
          members_price: members,
          typical_price: typical,
          url: `${siteUrl}${getPDPUrl(variant, locale)}`,
        },
      },
      { event_id: eventId }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //The setTimeout ensures the productView event fires only after the page fully loads.
  //Without it, the event wasn't triggering at all.
  //Making this a temporary workaround that should be revisited later.
  setTimeout(() => {
    productView(variant, currencyCode, eventId)
  }, 200)

  useEffect(() => {
    if (membershipId && typeof membershipId === "string") {
      addMembershipById(membershipId)
    }
  }, [membershipId])

  const hasMounted = useHasMounted()

  return (
    <>
      <Flex w="full" direction="column" align="center">
        {confirmation && (
          <ATBConfirmation
            source="PDP"
            confirmation={confirmation}
            setConfirmation={setConfirmation}
            variant={variant}
          />
        )}
        {preview && <PreviewAlertBox />}
        <Box
          display={{ base: "block", lg: "grid" }}
          w="full"
          mb={{ base: 5, md: 6.5 }}
          maxW="90rem"
          px={{ lg: 10, xl: "7.5rem" }}
          gap={3}
          gridTemplateColumns={{
            lg: "[start] 2.1fr [content] 1fr",
          }}
          gridTemplateRows={{
            lg: "[start] auto [content] auto [end] auto",
          }}
          position="relative"
          zIndex="1"
        >
          <Box gridColumnStart={{ lg: "start" }}>
            <GalleryLayout>
              <ProductGalleryV2 variant={variant} />
            </GalleryLayout>
          </Box>

          <Box
            position={{
              lg: "sticky",
            }}
            top={{
              lg: "45px",
            }}
            px={{ base: 2, md: 10, lg: 0 }}
          >
            <Box>
              <Box pt={{ base: 0, md: 2, lg: 7 }} pb={{ base: 0, lg: 3 }}>
                <ErrorBoundary
                  FallbackComponent={() => (
                    <Box textAlign="center" pt={{ base: 1, md: 0 }}>
                      <PDPHeaderFallback />
                    </Box>
                  )}
                  onError={(error) =>
                    Logger.warn(`PDP product info failed to load. ${error}`)
                  }
                >
                  <ProductInfoLayout hasInfo={false}>
                    <Box>
                      <Text
                        fontSize="1.75rem"
                        lineHeight="2.5rem"
                        m={0}
                        as="h1"
                      >
                        <Text variant="acuminMedium">{variant.brandName}</Text>
                        <Text
                          variant="acuminLight"
                          mt={0}
                        >{`${variant.title}`}</Text>
                      </Text>

                      {variant.longDescription && (
                        <ReadMoreV2
                          text={variant.longDescription}
                          suitableFor=""
                          isGiftMembership
                        />
                      )}
                    </Box>
                    {
                      hasMounted ? (
                        <Pricing
                          variant={variant}
                          setConfirmation={setConfirmation}
                          purchaseType={purchaseType}
                          setPurchaseType={setPurchaseType}
                        />
                      ) : (
                        <Box w="full" h="1rem" />
                      ) // placeholder
                    }
                  </ProductInfoLayout>
                </ErrorBoundary>
              </Box>
            </Box>
          </Box>
        </Box>
      </Flex>

      <LoadingMask />
    </>
  )
}
